import React from 'react';
import { gemeinsameEinrichtungen } from '../handbuchLogic';
import type { ReportMetadata } from '../../../types';
import type { Arbeitsschritt } from '../../sige-plan/types';
import type { Handbuch } from '../types';
import { A3Seite } from './A3Seite';
import { Abschnitt, AbschnittLeer, AbschnittTabelle, AbschnittZeile, AbschnittZelle } from './Abschnitt';
import { HANDBUCH_FARBEN } from './stil';

/**
 * Kapitel 3 — Baustelleneinrichtung.
 *
 * Portiert aus `pages/editor/BaustelleneinrichtungPage.tsx` der Quell-App
 * (153 Z.): oben der Plan mit Legende, unten drei Spalten. Getauscht sind die
 * Datenquellen.
 *
 * ═══ DIESES KAPITEL SCHREIBT NICHTS ════════════════════════════════════════
 * Der Lageplan bleibt, wo er ist (`metadata.plans`), die gemeinsam genutzten
 * Einrichtungen werden aus den Arbeitsschritten der Planfassung ABGELEITET,
 * und die Erlaeuterungen kommen aus `handbuecher.einrichtung`. Keine dieser
 * Quellen wird von hier aus veraendert — Praezedenzfall im Repo ist die
 * Vorankuendigung („liest die Projektdaten nur, schreibt nie hinein").
 *
 * ═══ WOHER DIE GEMEINSAMEN EINRICHTUNGEN KOMMEN ════════════════════════════
 * Nicht aus einer eigenen Liste, sondern aus den Arbeitsschritten mit einem
 * Hauptabschnitt „gemeinsam genutzte Einrichtung: …" (`sige-plan/types.ts`,
 * `HAUPTABSCHNITTE`). Eine zweite, von Hand gepflegte Liste waere binnen
 * weniger Wochen etwas anderes als der Plan, und dann stritten zwei Papiere
 * darueber, wer das Geruest mitbenutzt.
 */

type SprungZiel = 'lageplan' | 'sigeplan' | 'handbuch' | 'stammdaten';

interface Props {
    /** Projektstammdaten — Lagepläne (`metadata.plans`), Name, Bauherr. */
    metadata: ReportMetadata;
    /** Handbuchkopf: Erläuterungen (`einrichtung`) und Zugangsregelung
     *  (`ordnung.zugang`) werden hier nur GELESEN. */
    handbuch: Handbuch | null;
    /** Arbeitsschritte der Planfassung. Ohne sie bleibt die Aufstellung der
     *  gemeinsamen Einrichtungen leer — und sagt das auch. */
    schritte?: Arbeitsschritt[];
    /** Welcher Lageplan gezeigt wird. Fehlt die Angabe, wird der erste
     *  genommen; die uebrigen werden gezaehlt, nicht verschwiegen. */
    planId?: string;
    /** `globalData.locationCoordinates` — steht als Ortsangabe im Hinweiskasten. */
    koordinaten?: { lat: number; lng: number };
    /** Kopfzeile der Seite, ueblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fusszeile der Seite, ueblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    onSprung?: (ziel: SprungZiel) => void;
}

/**
 * Legende zum Baustelleneinrichtungsplan.
 *
 * 1:1 aus `BaustelleneinrichtungPage.tsx:8-21` uebernommen. Sie beschreibt
 * die Zeichenweise des BE-Plans und nicht die Daten dieses Moduls — deshalb
 * eine Konstante und kein Datensatz.
 */
const LEGENDE: { symbol: string; farbe: string; text: string }[] = [
    { symbol: '●', farbe: '#d946ef', text: 'Schwenkbereich Kran' },
    { symbol: '■', farbe: '#2563eb', text: 'Baufeld / Gebäude' },
    { symbol: '■', farbe: '#ea580c', text: 'Baucontainer / Sozialräume' },
    { symbol: '■', farbe: '#16a34a', text: 'Lagerflächen / Stellflächen' },
    { symbol: '→', farbe: '#333333', text: 'Zufahrt / Rettungszufahrt' },
    { symbol: '⚡', farbe: '#d97706', text: 'Baustrom-Verteiler' },
    { symbol: '◆', farbe: '#2563eb', text: 'Bauwasser-Anschluss' },
    { symbol: '▲', farbe: '#b91c1c', text: 'Sammelplatz' },
    { symbol: '■', farbe: '#555555', text: 'Abfallcontainer / Entsorgung' },
    { symbol: '---', farbe: '#333333', text: 'Bauzaun / Absperrung' },
    { symbol: '☆', farbe: '#333333', text: 'Kran-Standort' },
    { symbol: '■', farbe: '#fbbf24', text: 'Verkehrswege / Baustraße' },
];

// ═══════════════════════════════════════════════════════════════════════════
// BAUSTEINE
//
// `gemeinsameEinrichtungen`, `eindeutig` und `verbinde` liegen in
// `handbuchLogic.ts` — dort sind sie geprueft (das Projekt uebersetzt Tests
// ohne jsdom, eine Komponente ist nicht pruefbar). Hier bleibt nur, was allein
// dieses Blatt darstellt.
// ═══════════════════════════════════════════════════════════════════════════

/** Eine benannte Luecke mit Sprung. Der Satz bleibt im Druck, der Knopf nicht. */
const Luecke: React.FC<{
    text: string;
    ziel?: SprungZiel;
    onSprung?: (ziel: SprungZiel) => void;
}> = ({ text, ziel, onSprung }) => (
    <div className="flex flex-col items-start gap-1">
        <AbschnittLeer text={text} />
        {ziel && onSprung && (
            <button
                type="button"
                onClick={() => onSprung(ziel)}
                className="print:hidden underline"
                style={{ fontSize: '8px', color: HANDBUCH_FARBEN.textLeise }}
            >
                → dorthin wechseln
            </button>
        )}
    </div>
);

/**
 * Eine Zeile „Bezeichnung: Wert" mit ehrlichem Ersatztext.
 *
 * Fehlt der Wert, steht daneben ein Sprung dorthin, wo er erfasst wird. Ohne
 * ihn sagte das Blatt zwar richtig, dass etwas fehlt, aber nicht, wo man es
 * hinschreibt — und die Uebersicht des Handbuchs ist nicht der erste Ort, an
 * dem man es sucht. Der Knopf verschwindet im Druck (`print:hidden`), der
 * Satz bleibt.
 */
const Angabe: React.FC<{
    bezeichnung: string;
    wert?: string;
    fehlt: string;
    ziel?: SprungZiel;
    onSprung?: (ziel: SprungZiel) => void;
}> = ({ bezeichnung, wert, fehlt, ziel, onSprung }) => (
    <div style={{ fontSize: '8.5px', lineHeight: 1.5 }}>
        <span style={{ fontWeight: 600 }}>{bezeichnung}: </span>
        {wert?.trim()
            ? <span style={{ whiteSpace: 'pre-wrap' }}>{wert.trim()}</span>
            : (
                <>
                    <span style={{ fontStyle: 'italic', color: HANDBUCH_FARBEN.statusOffen }}>{fehlt}</span>
                    {ziel && onSprung && (
                        <button
                            type="button"
                            onClick={() => onSprung(ziel)}
                            className="print:hidden underline ml-1"
                            style={{ fontSize: '8px', color: HANDBUCH_FARBEN.textLeise }}
                        >
                            → erfassen
                        </button>
                    )}
                </>
            )}
    </div>
);

export const Baustelleneinrichtung: React.FC<Props> = ({
    metadata, handbuch, schritte = [], planId, koordinaten, kopf, fuss, onSprung,
}) => {
    const plaene = metadata.plans || [];
    const plan = (planId ? plaene.find(p => p.id === planId) : undefined) ?? plaene[0];
    const einrichtungen = gemeinsameEinrichtungen(schritte);
    const angaben = handbuch?.einrichtung;

    return (
        <A3Seite kopf={kopf} fuss={fuss}>
            <div className="flex-1 flex flex-col gap-[6px] min-h-0 overflow-hidden">

                {/* ═══ OBEN: Plan und Legende ═══ */}
                <div className="flex gap-[6px] min-h-0 overflow-hidden" style={{ flex: '1 1 0', maxHeight: '68%' }}>

                    <Abschnitt
                        titel="Baustelleneinrichtungsplan"
                        zusatz={plan ? plan.name : undefined}
                        className="min-h-0"
                        style={{ width: '75%' }}
                    >
                        {plan?.image ? (
                            <div className="flex items-center justify-center overflow-hidden" style={{ height: '100%' }}>
                                <img
                                    src={plan.image}
                                    alt={plan.name || 'Baustelleneinrichtungsplan'}
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                />
                            </div>
                        ) : (
                            /* Die Vorlage schrieb hier „wird separat als Anlage
                               beigefuegt" — eine Behauptung ueber ein Blatt, das
                               niemand gesehen hat. Wir sagen stattdessen, was
                               fehlt und wo es hingehoert. */
                            <Luecke
                                text="Für dieses Projekt ist kein Lageplan hinterlegt. Der Baustelleneinrichtungsplan gehört in die Projekt-Lagepläne; von dort zieht ihn dieses Kapitel."
                                ziel="lageplan"
                                onSprung={onSprung}
                            />
                        )}
                    </Abschnitt>

                    <div className="flex flex-col gap-[6px] min-h-0" style={{ width: '25%' }}>
                        <Abschnitt titel="Legende" className="flex-1">
                            <div style={{ fontSize: '8px', lineHeight: 1.7 }}>
                                {LEGENDE.map(e => (
                                    <div key={e.text} className="flex items-center gap-[4px]">
                                        <span style={{
                                            color: e.farbe,
                                            fontSize: '10px',
                                            width: '14px',
                                            textAlign: 'center',
                                            flexShrink: 0,
                                        }}>
                                            {e.symbol}
                                        </span>
                                        <span>{e.text}</span>
                                    </div>
                                ))}
                            </div>
                        </Abschnitt>

                        <Abschnitt titel="Hinweise" style={{ flex: '0 0 auto' }}>
                            <div style={{ fontSize: '8px', lineHeight: 1.6, color: HANDBUCH_FARBEN.textLeise }}>
                                <p>Maßstab und Nordpfeil siehe Plan.</p>
                                <p style={{ marginTop: '3px' }}>
                                    Der BE-Plan ist bei wesentlichen Änderungen der Baustelleneinrichtung
                                    fortzuschreiben.
                                </p>
                                <p style={{ marginTop: '3px' }}>
                                    Rettungswege und Feuerwehrzufahrten sind jederzeit freizuhalten
                                    (mindestens 3,5 m Breite).
                                </p>
                                {koordinaten && (
                                    <p style={{ marginTop: '3px' }}>
                                        Lage: {koordinaten.lat.toFixed(5)} / {koordinaten.lng.toFixed(5)}
                                    </p>
                                )}
                                {plaene.length > 1 && (
                                    <p style={{ marginTop: '3px' }}>
                                        Im Projekt liegen {plaene.length} Pläne; hier ist einer abgebildet.
                                    </p>
                                )}
                            </div>
                        </Abschnitt>
                    </div>
                </div>

                {/* ═══ UNTEN: drei Spalten ═══ */}
                <div className="flex gap-[6px]" style={{ flex: '0 0 auto', maxHeight: '32%' }}>

                    <Abschnitt
                        titel="Gemeinsam genutzte Einrichtungen"
                        zusatz={einrichtungen.length ? `${einrichtungen.length}` : undefined}
                        className="flex-1"
                    >
                        {einrichtungen.length > 0 ? (
                            <AbschnittTabelle
                                kopf={['Einrichtung', 'Mitnutzer', 'Verantwortlich']}
                                breiten={['30%', '44%', '26%']}
                            >
                                {einrichtungen.map((e, i) => (
                                    <AbschnittZeile key={e.bezeichnung} erste={i === 0}>
                                        <AbschnittZelle style={{ fontSize: '8px', fontWeight: 600 }}>
                                            {e.bezeichnung}
                                        </AbschnittZelle>
                                        <AbschnittZelle style={{ fontSize: '8px' }}>
                                            {e.ohneMitnutzer ? (
                                                <span style={{ fontStyle: 'italic', color: HANDBUCH_FARBEN.statusOffen }}>
                                                    kein Gewerk zugeordnet
                                                </span>
                                            ) : e.mitnutzer.join(', ')}
                                        </AbschnittZelle>
                                        <AbschnittZelle style={{ fontSize: '8px' }}>
                                            {e.verantwortlich.length ? e.verantwortlich.join(', ') : (
                                                <span style={{ fontStyle: 'italic', color: HANDBUCH_FARBEN.statusOffen }}>
                                                    offen
                                                </span>
                                            )}
                                        </AbschnittZelle>
                                    </AbschnittZeile>
                                ))}
                            </AbschnittTabelle>
                        ) : (
                            <Luecke
                                text={schritte.length
                                    ? 'Im Plan trägt kein Arbeitsschritt einen Hauptabschnitt „gemeinsam genutzte Einrichtung". Verkehrswege, Lagerplätze, Gerüste und Arbeitsschutz sind dort einzuordnen — erst dann erscheinen sie hier.'
                                    : 'Es sind keine Arbeitsschritte geladen. Die gemeinsam genutzten Einrichtungen leitet dieses Kapitel aus dem SiGe-Plan ab.'}
                                ziel="sigeplan"
                                onSprung={onSprung}
                            />
                        )}
                    </Abschnitt>

                    <Abschnitt titel="Verkehrswege & Zugang" className="flex-1">
                        <Angabe
                            bezeichnung="Zugangsregelung"
                            wert={handbuch?.ordnung?.zugang}
                            fehlt="im Kapitel Baustellenordnung noch nicht festgelegt"
                            ziel="handbuch"
                            onSprung={onSprung}
                        />
                        <Angabe
                            bezeichnung="Verkehrsführung"
                            wert={angaben?.verkehrsfuehrung}
                            fehlt="noch nicht erfasst"
                            ziel="handbuch"
                            onSprung={onSprung}
                        />
                        <Angabe
                            bezeichnung="Lagerplatz"
                            wert={angaben?.lagerplatz}
                            fehlt="noch nicht erfasst"
                            ziel="handbuch"
                            onSprung={onSprung}
                        />
                    </Abschnitt>

                    <Abschnitt titel="Ver- und Entsorgung" className="flex-1">
                        <Angabe
                            bezeichnung="Versorgung"
                            wert={angaben?.versorgung}
                            fehlt="Baustrom, Bauwasser, Beleuchtung noch nicht erfasst"
                            ziel="handbuch"
                            onSprung={onSprung}
                        />
                        <Angabe
                            bezeichnung="Entsorgung"
                            wert={angaben?.entsorgung}
                            fehlt="Abfall und Abwasser noch nicht erfasst"
                            ziel="handbuch"
                            onSprung={onSprung}
                        />
                        {angaben?.bemerkung?.trim() && (
                            <div style={{ fontSize: '8.5px', lineHeight: 1.5, marginTop: '3px', whiteSpace: 'pre-wrap' }}>
                                {angaben.bemerkung.trim()}
                            </div>
                        )}
                    </Abschnitt>
                </div>
            </div>
        </A3Seite>
    );
};
